import React, { useState, useEffect } from 'react';
// IMPORTANTE: Traemos useParams para leer la URL de la vacante
import { useParams, useNavigate } from 'react-router-dom'; 
import { 
  Briefcase, MapPin, DollarSign, Clock, Search, 
  UploadCloud, X, CheckCircle2, ArrowRight, Building2, Loader2, ChevronDown 
} from 'lucide-react';

// --- DATOS MOCKEADOS (Fallback en caso de error) ---
const VACANTES_MOCK = [
  {
    id: 1,
    title: { rendered: "Analista de Nómina Senior (Ejemplo)" },
    slug: "analista-nomina-senior", // Agregado slug al mock
    ubicacion: "Bogotá",
    modalidad: "Híbrido",
    salario: "$2.500.000",
    content: { rendered: "<p>Esta es una vacante de ejemplo. Publica tus vacantes reales desde el panel de WordPress > Vacantes Zaphy.</p>" },
    requisitos_lista: "Contador Público,Experiencia 1 año,Excel Avanzado",
    date: new Date().toISOString()
  }
];

const TrabajaConNosotros = ({ language }) => { 
  
  // 1. OBTENER SLUG DE LA URL
  const { slug } = useParams();
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]); 
  const [loading, setLoading] = useState(true); 
  
  // --- ESTADOS DE FILTROS ---
  const [searchTerm, setSearchTerm] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterMode, setFilterMode] = useState("");

  // --- ESTADOS DEL FORMULARIO ---
  const [selectedJob, setSelectedJob] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  // --- 1. CONEXIÓN CON WORDPRESS (NUEVO ENDPOINT NATIVO) ---
  useEffect(() => {
    const fetchVacantes = async () => {
      try {
        const siteUrl = window.zaphyData?.siteUrl || 'https://zaphy.com.co'; 
        
        // Consultamos nuestro Custom Post Type propio creado en el plugin PHP
        // Endpoint: /wp-json/wp/v2/vacantes-api
        // Traemos más vacantes para asegurar encontrar el slug si venimos por URL
        const response = await fetch(`${siteUrl}/wp-json/wp/v2/vacantes-api?_embed&per_page=100`);
        
        if (!response.ok) throw new Error("Error conectando con API Vacantes");
        
        const data = await response.json();
        
        if (Array.isArray(data) && data.length > 0) {
            setJobs(data);
        } else {
            console.log("No hay vacantes publicadas aún.");
            setJobs([]); 
        }
      } catch (error) {
        console.error("Error cargando vacantes, usando demostración:", error);
        setJobs(VACANTES_MOCK); // Fallback en caso de error técnico
      } finally {
        setLoading(false);
      }
    };

    fetchVacantes();
  }, []);

  // --- 2. FORMATEO DE DATOS ---
  const formatJobData = (wpJob) => {
      const title = wpJob.title?.rendered || "Sin título";
      
      // Fecha relativa (Hace X días)
      const dateDiff = Math.floor((new Date() - new Date(wpJob.date)) / (1000 * 60 * 60 * 24));
      const publicado = dateDiff === 0 ? "Hoy" : `Hace ${dateDiff} días`;

      // Requisitos: Convertir string separado por comas a array
      const rawReqs = wpJob.requisitos_lista || ""; 
      const requisitosArray = rawReqs 
          ? rawReqs.split(',').map(r => r.trim()) 
          : ["Ver descripción completa"];

      return {
          id: wpJob.id,
          slug: wpJob.slug, // Guardamos el slug para comparar
          cargo: title,
          empresa: "Zaphy Talento", 
          ubicacion: wpJob.ubicacion || "Colombia",
          modalidad: wpJob.modalidad || "Presencial",
          salario: wpJob.salario || "A convenir",
          publicado: publicado,
          descripcion: wpJob.content?.rendered || "",
          requisitos: requisitosArray,
          // Guardamos el título limpio para el formulario (sin HTML tags)
          rawTitle: title.replace(/<[^>]+>/g, '') 
      };
  };

  const formattedJobs = jobs.map(formatJobData);
  
  // Extraer valores únicos para filtros dinámicos
  const uniqueLocations = [...new Set(formattedJobs.map(j => j.ubicacion).filter(Boolean))];
  const uniqueModes = [...new Set(formattedJobs.map(j => j.modalidad).filter(Boolean))];

  // --- 3. FILTRADO ---
  const filteredJobs = formattedJobs.filter(job => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = job.cargo.toLowerCase().includes(term) || 
                          job.empresa.toLowerCase().includes(term);
    const matchesLocation = filterLocation ? job.ubicacion === filterLocation : true;
    const matchesMode = filterMode ? job.modalidad === filterMode : true;

    return matchesSearch && matchesLocation && matchesMode;
  });

  // --- EFECTO MAGICO: ABRIR MODAL SI HAY URL (SEO) ---
  useEffect(() => {
      if (slug && jobs.length > 0) {
          // Buscamos la vacante que coincida con el slug de la URL
          const vacanteEncontrada = jobs.find(job => job.slug === slug);
          
          if (vacanteEncontrada) {
              // Convertimos al formato interno y abrimos el modal
              setSelectedJob(formatJobData(vacanteEncontrada));
          }
      }
  }, [slug, jobs]); // Se ejecuta cuando llegan los jobs o cambia la URL

  // --- HANDLER PARA CERRAR MODAL ---
  const handleCloseModal = () => {
    if (!isSubmitting) {
        setSelectedJob(null);
        // Si estábamos en una URL específica (/vacante/slug), volvemos a la lista general
        if (slug) {
            navigate('/trabaja-con-nosotros');
        }
    }
  };

  // --- 4. ENVÍO FORMULARIO A CONTACT FORM 7 ---
  const handleApply = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);

    const formData = new FormData(e.target);
    const formId = '7719'; // ID del formulario CF7 configurado previamente
    
    const apiData = new FormData();
    apiData.append('your-name', formData.get('nombre'));
    apiData.append('your-email', formData.get('email'));
    apiData.append('your-phone', formData.get('celular'));
    apiData.append('your-message', formData.get('mensaje'));
    apiData.append('job-ref', selectedJob.rawTitle); // Enviamos el título de la vacante al campo oculto
    apiData.append('resume-file', formData.get('archivo')); 

    // Campos ocultos requeridos por CF7 para aceptar la petición REST
    apiData.append('_wpcf7', formId);
    apiData.append('_wpcf7_unit_tag', `wpcf7-f${formId}-p1-o1`);

    try {
        const siteUrl = window.zaphyData?.siteUrl || 'https://zaphy.com.co'; 
        
        const response = await fetch(`${siteUrl}/wp-json/contact-form-7/v1/contact-forms/${formId}/feedback`, {
            method: 'POST',
            body: apiData
        });

        const result = await response.json();

        if (result.status === 'mail_sent') {
            setSubmitSuccess(true);
            setTimeout(() => {
                setSubmitSuccess(false);
                handleCloseModal(); // Usamos el handler para limpiar URL si es necesario
            }, 3000);
        } else {
            console.error("Error CF7:", result);
            setSubmitError(true); 
        }
    } catch (error) {
        console.error("Error de red:", error);
        setSubmitError(true);
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <div className="animate-[fadeIn_0.5s_ease-out] pt-32 pb-20 min-h-screen bg-gray-50 font-[Poppins]">
      
      {/* --- HERO SECTION --- */}
      <div className="relative bg-[#0F172A] text-white py-20 overflow-hidden mb-12">
         <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80')] bg-cover bg-center"></div>
         <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-purple-900/60"></div>
         
         <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
            <span className="inline-block py-1 px-3 rounded-full bg-purple-500/20 border border-purple-400/30 text-purple-300 text-sm font-bold mb-4">
               Portal de Empleo Zaphy
            </span>
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6">
               Tu próximo reto profesional <br/>
               <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">está aquí</span>
            </h1>

            {/* BUSCADOR PRINCIPAL */}
            <div className="max-w-3xl mx-auto bg-white p-2 rounded-full shadow-2xl flex items-center mb-8">
               <div className="pl-6 text-gray-400">
                  <Search size={20} />
               </div>
               <input 
                  type="text" 
                  placeholder="Buscar cargo o palabra clave..." 
                  className="w-full p-4 outline-none text-gray-700 placeholder-gray-400 bg-transparent rounded-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
               />
               <button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-full font-bold transition-all hidden sm:block">
                  Buscar
               </button>
            </div>
         </div>
      </div>

      {/* --- ZONA DE FILTROS Y RESULTADOS --- */}
      <div className="max-w-7xl mx-auto px-4 mb-24">
         
         {/* BARRA DE FILTROS */}
         <div className="flex flex-wrap items-center justify-between gap-4 mb-8 bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
               <Briefcase className="text-purple-600"/> 
               Vacantes Disponibles ({filteredJobs.length})
            </h2>
            
            <div className="flex flex-wrap gap-2">
               {/* Filtro Ubicación */}
               <div className="relative group">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
                     <MapPin size={16} />
                  </div>
                  <select 
                     value={filterLocation}
                     onChange={(e) => setFilterLocation(e.target.value)}
                     className="appearance-none pl-9 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all cursor-pointer"
                  >
                     <option value="">Ubicación</option>
                     {uniqueLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
               </div>

               {/* Filtro Modalidad */}
               <div className="relative group">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
                     <Building2 size={16} />
                  </div>
                  <select 
                     value={filterMode}
                     onChange={(e) => setFilterMode(e.target.value)}
                     className="appearance-none pl-9 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all cursor-pointer"
                  >
                     <option value="">Modalidad</option>
                     {uniqueModes.map(mode => <option key={mode} value={mode}>{mode}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
               </div>
               
               {/* Botón limpiar filtros */}
               {(filterLocation || filterMode || searchTerm) && (
                  <button 
                     onClick={() => { setFilterLocation(""); setFilterMode(""); setSearchTerm(""); }}
                     className="text-xs text-red-500 hover:text-red-700 font-medium underline px-2"
                  >
                     Limpiar
                  </button>
               )}
            </div>
         </div>

         {loading ? (
             <div className="flex justify-center py-20">
                 <div className="flex flex-col items-center text-purple-600">
                     <Loader2 size={40} className="animate-spin mb-4" />
                     <p className="font-medium text-gray-500">Buscando talento...</p>
                 </div>
             </div>
         ) : filteredJobs.length > 0 ? (
            <div className="grid gap-6">
               {filteredJobs.map((job) => (
                  <div key={job.id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-purple-200 transition-all group">
                     <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                        
                        <div className="flex-1">
                           <div className="flex items-start justify-between mb-2">
                              <div>
                                 <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-1 rounded-md mb-2 inline-block">
                                    {job.empresa}
                                 </span>
                                 <h3 className="text-xl font-bold text-gray-900 group-hover:text-purple-700 transition-colors" dangerouslySetInnerHTML={{__html: job.cargo}}></h3>
                              </div>
                           </div>
                           
                           <div className="flex flex-wrap gap-y-2 gap-x-6 text-sm text-gray-500 mb-4">
                              <span className="flex items-center gap-1"><MapPin size={16}/> {job.ubicacion}</span>
                              <span className="flex items-center gap-1"><Building2 size={16}/> {job.modalidad}</span>
                              <span className="flex items-center gap-1 text-green-600 font-medium"><DollarSign size={16}/> {job.salario}</span>
                              <span className="flex items-center gap-1"><Clock size={16}/> {job.publicado}</span>
                           </div>

                           {/* Descripción (Extracto) */}
                           <div className="text-gray-600 mb-4 line-clamp-2 text-sm" dangerouslySetInnerHTML={{__html: job.descripcion}}></div>

                           <div className="flex flex-wrap gap-2">
                              {job.requisitos.map((req, idx) => (
                                 <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                    {req}
                                 </span>
                              ))}
                           </div>
                        </div>

                        <div className="flex items-center">
                           <button 
                              onClick={() => setSelectedJob(job)}
                              className="w-full md:w-auto bg-white border-2 border-purple-600 text-purple-700 hover:bg-purple-600 hover:text-white font-bold py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                           >
                              Ver y Aplicar <ArrowRight size={18}/>
                           </button>
                        </div>
                     </div>
                  </div>
               ))}
            </div>
         ) : (
            <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-300">
               <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
                  <Search size={32} />
               </div>
               <h3 className="text-xl font-bold text-gray-800">No encontramos vacantes activas</h3>
               <p className="text-gray-500 mt-2">Intenta ajustar los filtros o vuelve más tarde.</p>
               <button 
                  onClick={() => { setFilterLocation(""); setFilterMode(""); setSearchTerm(""); }}
                  className="mt-4 text-purple-600 font-bold hover:underline"
               >
                  Ver todo
               </button>
            </div>
         )}
      </div>

      {/* --- MODAL DE APLICACIÓN --- */}
      {selectedJob && (
         <div className="fixed inset-0 z-[99999] flex items-start justify-center pt-20 md:pt-32 px-4 bg-black/80 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out] overflow-y-auto">
            
            <div className="bg-white rounded-3xl w-full max-w-2xl my-8 shadow-[0_0_50px_rgba(147,51,234,0.3)] relative border-2 border-purple-500 ring-4 ring-purple-500/20 flex flex-col">
               
               {/* Header Modal */}
               <div className="sticky top-0 bg-white border-b border-gray-100 p-6 flex justify-between items-start z-10 rounded-t-3xl">
                  <div>
                     <h3 className="text-2xl font-bold text-gray-900" dangerouslySetInnerHTML={{__html: selectedJob.cargo}}></h3>
                     <div className="flex flex-wrap gap-2 text-sm text-gray-500 mt-2">
                        <span className="flex items-center gap-1"><MapPin size={14}/> {selectedJob.ubicacion}</span>
                        <span className="flex items-center gap-1 ml-3"><DollarSign size={14}/> {selectedJob.salario}</span>
                     </div>
                  </div>
                  
                  {/* Botón de cerrar más visible */}
                  <button 
                     onClick={handleCloseModal}
                     className="p-2 bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 rounded-full transition-colors border border-gray-200"
                     title="Cerrar ventana"
                  >
                     <X size={24} />
                  </button>
               </div>

               <div className="p-8">
                  {/* Descripción Completa */}
                  <div className="mb-8 prose prose-purple text-gray-600 text-sm">
                      <h4 className="font-bold text-gray-800 text-lg mb-2">Descripción del Cargo</h4>
                      <div dangerouslySetInnerHTML={{__html: selectedJob.descripcion}}></div>
                  </div>

                  {submitSuccess ? (
                     <div className="text-center py-10 bg-green-50 rounded-2xl border border-green-100">
                        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                           <CheckCircle2 size={32} />
                        </div>
                        <h4 className="text-xl font-bold text-gray-900 mb-2">¡Postulación Exitosa!</h4>
                        <p className="text-gray-600">
                           Hemos recibido tu hoja de vida. Te contactaremos pronto.
                        </p>
                     </div>
                  ) : (
                     <form onSubmit={handleApply} className="space-y-5 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                        <h4 className="font-bold text-gray-800 border-b border-gray-200 pb-2">Tus Datos</h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div>
                              <label className="text-xs font-bold text-gray-500 mb-1 block">Nombre Completo</label>
                              <input name="nombre" required type="text" className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm" placeholder="Juan Pérez" />
                           </div>
                           <div>
                              <label className="text-xs font-bold text-gray-500 mb-1 block">Celular / WhatsApp</label>
                              <input name="celular" required type="tel" className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm" placeholder="300 123 4567" />
                           </div>
                        </div>

                        <div>
                           <label className="text-xs font-bold text-gray-500 mb-1 block">Correo Electrónico</label>
                           <input name="email" required type="email" className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm" placeholder="tu@email.com" />
                        </div>

                        <div>
                        <label className="text-xs font-bold text-gray-500 mb-1 block">Hoja de Vida (PDF, Word o Imagen)</label><input name="archivo" required type="file" 
                           // Aquí el cambio clave:
                           accept=".pdf,.doc,.docx,image/*" 
                           className="w-full p-2 bg-white border border-gray-200 rounded-xl text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100" 
                        />
                        <p className="text-[10px] text-gray-400 mt-1">Formatos admitidos: PDF, Word, JPG, PNG.</p>
                        </div>

                        <div>
                           <label className="text-xs font-bold text-gray-500 mb-1 block">Mensaje (Opcional)</label>
                           <textarea name="mensaje" className="w-full p-3 bg-white border border-gray-200 rounded-xl h-20 resize-none focus:ring-2 focus:ring-purple-500 outline-none text-sm" placeholder="Algo que quieras añadir..."></textarea>
                        </div>

                        {submitError && (
                           <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center font-medium">
                              Ocurrió un error al enviar. Por favor verifica tu conexión o el archivo.
                           </div>
                        )}

                        <button 
                           type="submit" 
                           disabled={isSubmitting}
                           className={`w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 ${isSubmitting ? 'opacity-70 cursor-wait' : ''}`}
                        >
                           {isSubmitting ? <><Loader2 className="animate-spin" size={20}/> Enviando...</> : 'Enviar mi Hoja de Vida'}
                        </button>
                     </form>
                  )}
               </div>
            </div>
         </div>
      )}

    </div>
  );
};

export default TrabajaConNosotros;